import { QuizQuestion } from "../types/quiz";

export type QuizSection = {
  id: string;
  title: string;
  description: string;
  questions: QuizQuestion[];
};
const artworkPrepQuestions: QuizQuestion[] = [
  {
    id: "art-1",
    category: "Artwork Prep",
    question:
      "A customer file looks fine at normal zoom, but small text and thin lines print rough and weak. What is the most likely issue to review first?",
    options: [
      "The garment platen pressure",
      "The artwork resolution and edge quality",
      "The powder brand",
      "The cold peel timing",
    ],
    correctAnswer: 1,
    explanation:
      "When fine detail breaks down, artwork resolution, edge cleanup, and source quality should be reviewed first before blaming the transfer stage.",
  },
  {
    id: "art-2",
    category: "Artwork Prep",
    question:
      "Why is a transparent background important when preparing many DTF files?",
    options: [
      "It increases press pressure automatically",
      "It replaces the white underbase",
      "It helps avoid printing unwanted background areas",
      "It locks the file into a print-only format",
    ],
    correctAnswer: 2,
    explanation:
      "Transparent backgrounds help prevent unwanted printed blocks or halos around the design.",
  },
  {
    id: "art-3",
    category: "Artwork Prep",
    question:
      "What is the main reason a white underbase matters so much on dark garments?",
    options: [
      "It prevents film skew",
      "It supports opacity and helps colors stay bright",
      "It reduces curing time",
      "It makes hot peel easier",
    ],
    correctAnswer: 1,
    explanation:
      "The white layer acts as a base that supports the top colors and helps them remain vibrant on dark materials.",
  },
  {
    id: "art-4",
    category: "Artwork Prep",
    question:
      "If the edges of a design already look jagged before printing, what is the smartest conclusion?",
    options: [
      "A final press will usually fix it",
      "More powder will hide the issue",
      "The printer will probably smooth them out during output",
      "The problem may carry into the final transfer unless the artwork is cleaned up",
    ],
    correctAnswer: 3,
    explanation:
      "Bad artwork edges often show up in the final result. Printing usually reveals prep problems rather than fixing them.",
  },
  {
    id: "art-5",
    category: "Artwork Prep",
    question:
      "Which file-prep habit is usually safer before running automated actions on artwork?",
    options: [
      "Flatten everything immediately",
      "Resize the file down before editing",
      "Work on a duplicate or preserve the original file",
      "Convert all colors to grayscale first",
    ],
    correctAnswer: 2,
    explanation:
      "Automated actions can change layers, selections, and file structure, so protecting the original is a smart workflow habit.",
  },
  {
    id: "art-6",
    category: "Artwork Prep",
    question:
      "A spot channel named W1 in Photoshop is most commonly associated with what in DTF workflows?",
    options: [
      "A contour cut path",
      "A white ink layer definition",
      "A press calibration layer",
      "A compression mask",
    ],
    correctAnswer: 1,
    explanation:
      "A W1 or similarly named spot channel is commonly used to define the white layer for DTF output workflows.",
  },
  {
    id: "art-7",
    category: "Artwork Prep",
    question:
      "Which situation is most likely to create a visible dirty halo around artwork after printing?",
    options: [
      "Using consistent press pressure",
      "A proper white underbase",
      "Strong edge cleanup with a transparent background",
      "Loose background debris or poorly cleaned semi-transparent pixels",
    ],
    correctAnswer: 3,
    explanation:
      "Stray pixels and dirty edge transparency often show up as unwanted printed haze or halos.",
  },
  {
    id: "art-8",
    category: "Artwork Prep",
    question:
      "What is generally the safest target resolution for print-ready DTF artwork in many workflows?",
    options: ["150 DPI", "200 DPI", "1200 DPI minimum", "300 DPI"],
    correctAnswer: 3,
    explanation:
      "300 DPI is a common working standard for sharp, production-ready DTF artwork.",
  },
  {
    id: "art-9",
    category: "Artwork Prep",
    question:
      "A file has beautiful gradients on screen, but the customer also wants strong durability and clean edges. What should you pay close attention to during prep?",
    options: [
      "Whether the garment is folded",
      "Only the title of the file",
      "How fades, semi-transparency, and edge behavior will translate to print",
      "Whether the monitor brightness is high",
    ],
    correctAnswer: 2,
    explanation:
      "Gradients and transparency effects can look great on screen but need thoughtful prep so they translate well to printed output.",
  },
  {
    id: "art-10",
    category: "Artwork Prep",
    question:
      "When preparing a print file, which answer best describes why tiny isolated specks should be removed?",
    options: [
      "They reduce powder adhesion only on cotton",
      "They only matter on white shirts",
      "They make the heat press too hot",
      "They can print as unwanted dots or debris in the final transfer",
    ],
    correctAnswer: 3,
    explanation:
      "Small stray marks can become visible printed defects, especially if they catch white or color output.",
  },
  {
    id: "art-11",
    category: "Artwork Prep",
    question:
      "Why is enlarging a very small low-quality image usually risky for DTF output?",
    options: [
      "Because it removes the need for cleanup",
      "Because enlarging poor source art can keep edges soft and defects visible",
      "Because it automatically improves fine detail",
      "Because it replaces white underbase prep",
    ],
    correctAnswer: 1,
    explanation:
      "Upscaling may make the file larger, but it does not restore missing detail or clean edges automatically.",
  },
  {
    id: "art-12",
    category: "Artwork Prep",
    question:
      "What is a strong reason to inspect semi-transparent edge fades before production?",
    options: [
      "They prevent powder flow",
      "They increase platen pressure",
      "They may print differently than expected and affect edge cleanliness",
      "They stop white circulation",
    ],
    correctAnswer: 2,
    explanation:
      "Soft transparency can translate differently in print and may create unwanted edge haze or weak transitions.",
  },
  {
    id: "art-13",
    category: "Artwork Prep",
    question:
      "If a customer sends a screenshot instead of original artwork, what is the smartest first concern?",
    options: [
      "The press temperature will need to be lower",
      "The garment will not need white ink",
      "The source may have poor resolution, compression, or dirty edges",
      "The powder stage will become irrelevant",
    ],
    correctAnswer: 2,
    explanation:
      "Screenshots often contain compression, soft edges, and lower usable quality than original design files.",
  },
  {
    id: "art-14",
    category: "Artwork Prep",
    question:
      "Why is edge cleanup important even when the main design looks bold and easy to print?",
    options: [
      "Because it changes pressure automatically",
      "Because hidden junk around the design can still print as haze or dirty debris",
      "Because it reduces garment moisture",
      "Because it prevents film skew by itself",
    ],
    correctAnswer: 1,
    explanation:
      "Even bold artwork can carry background junk, semi-transparent trash, or loose debris that becomes visible in output.",
  },
  {
    id: "art-15",
    category: "Artwork Prep",
    question:
      "What is one reason tiny text should be checked carefully before production?",
    options: [
      "Because fine details may break down if the source quality or prep is weak",
      "Because it changes the garment fabric",
      "Because tiny text increases cure heat automatically",
      "Because small text always needs extra powder",
    ],
    correctAnswer: 0,
    explanation:
      "Fine text is one of the fastest ways to expose poor source quality, weak resolution, or rough edge prep.",
  },
  {
    id: "art-16",
    category: "Artwork Prep",
    question:
      "Which answer best explains why artwork prep affects troubleshooting later?",
    options: [
      "Because the RIP fixes all artwork issues automatically",
      "Because poor prep can create defects that look like print or press problems later",
      "Because prep only matters for customer approval",
      "Because prep never affects the final transfer",
    ],
    correctAnswer: 1,
    explanation:
      "Bad artwork can survive through the workflow and show up later as defects that get blamed on other stages.",
  },
  {
    id: "art-17",
    category: "Artwork Prep",
    question:
      "If a design has rough anti-aliased edges and junk transparency, what should you expect if you print it as-is?",
    options: [
      "Possible haze, weak edges, and a dirtier final look",
      "Automatic sharpening during output",
      "Lower press pressure requirement",
      "Stronger hot peel behavior",
    ],
    correctAnswer: 0,
    explanation:
      "Messy edge data often translates into weak or dirty printed edges instead of fixing itself during production.",
  },
  {
    id: "art-18",
    category: "Artwork Prep",
    question:
      "Why is preserving the original layered artwork valuable even after making a print-ready version?",
    options: [
      "Because it prevents all color issues",
      "Because the press requires layered files",
      "Because future edits are easier and cleaner if the original remains untouched",
      "Because powder only works on layered art",
    ],
    correctAnswer: 2,
    explanation:
      "Keeping the original file intact gives you a clean base if a client wants revisions or prep needs to be redone.",
  },
  {
    id: "art-19",
    category: "Artwork Prep",
    question:
      "A design looks sharp overall, but some faded outer glow effects seem questionable. What is the smartest mindset?",
    options: [
      "Assume powder will hide them",
      "Lower the artwork DPI on purpose",
      "Ignore them because the press will fix it",
      "Review how those effects will translate to print instead of trusting the screen only",
    ],
    correctAnswer: 3,
    explanation:
      "Screen effects can behave differently in print, especially when transparency and soft edges are involved.",
  },
  {
    id: "art-20",
    category: "Artwork Prep",
    question:
      "What is one of the best reasons to create a standard artwork prep checklist?",
    options: [
      "It makes all garments behave the same",
      "It helps catch repeat mistakes before they become printed defects",
      "It guarantees perfect pressure settings",
      "It eliminates the need for nozzle checks",
    ],
    correctAnswer: 1,
    explanation:
      "A repeatable checklist helps reduce avoidable prep mistakes and creates a more consistent production workflow.",
  },
];

const printingQuestions: QuizQuestion[] = [
  {
    id: "print-1",
    category: "Printing",
    question:
      "A print shows repeated horizontal inconsistency across the image. Which cause is the most likely first suspect?",
    options: [
      "A transparent background",
      "A final press that was too short",
      "Nozzle issues or print stability problems",
      "The product description on the website",
    ],
    correctAnswer: 2,
    explanation:
      "Horizontal banding or repeated inconsistency often points to nozzle performance or print stability before transfer-stage causes.",
  },
  {
    id: "print-2",
    category: "Printing",
    question: "Why are nozzle checks so important before running production?",
    options: [
      "They prevent powder from clumping",
      "They replace maintenance",
      "They guarantee perfect color matching",
      "They confirm that channels are firing correctly",
    ],
    correctAnswer: 3,
    explanation:
      "Nozzle checks help confirm channel health and can catch printing issues before material is wasted.",
  },
  {
    id: "print-3",
    category: "Printing",
    question: "Why does white ink usually demand closer attention than other channels?",
    options: [
      "It is mainly decorative and optional",
      "It eliminates the need for nozzle checks",
      "It tends to be more maintenance-sensitive and consistency-sensitive",
      "It is unaffected by settling or circulation",
    ],
    correctAnswer: 2,
    explanation:
      "White ink often requires careful circulation, maintenance, and observation because it is usually the most temperamental channel.",
  },
  {
    id: "print-4",
    category: "Printing",
    question:
      "If film tracking is slightly off during a print run, what kind of issue can that create?",
    options: [
      "An automatic cure problem",
      "Skewing, drift, or placement inconsistency",
      "Hot peel behavior",
      "A stronger white underbase",
    ],
    correctAnswer: 1,
    explanation:
      "Film tracking issues can cause skew or alignment drift and create visible inconsistency across the print.",
  },
  {
    id: "print-5",
    category: "Printing",
    question:
      "Once a machine is producing consistently, why is it usually smart to avoid random setting changes?",
    options: [
      "Because unnecessary changes create new variables and make troubleshooting harder",
      "Because maintenance stops mattering",
      "Because every material behaves identically",
      "Because color profiles never affect output",
    ],
    correctAnswer: 0,
    explanation:
      "Changing working settings without reason creates noise in the workflow and makes problem-solving harder later.",
  },
  {
    id: "print-6",
    category: "Printing",
    question:
      "A print starts strong, then gradually shows instability as the job continues. Which answer is the best general first thought?",
    options: [
      "The press pressure later changed the printed film",
      "The file is probably in the wrong folder",
      "Something in the print system may be becoming inconsistent during the run",
      "The design title is too long",
    ],
    correctAnswer: 2,
    explanation:
      "If output changes during the run, think about print stability, feed, ink delivery, or consistency across time.",
  },
  {
    id: "print-7",
    category: "Printing",
    question:
      "Which answer best explains why print consistency matters even when a single test print looked okay?",
    options: [
      "Because a process must hold steady over multiple prints, not just one lucky sample",
      "Because powder application becomes irrelevant",
      "Because one good print proves the system is permanently fixed",
      "Because garment type never changes results",
    ],
    correctAnswer: 0,
    explanation:
      "A process should be repeatable. One decent print does not automatically confirm a stable workflow.",
  },
  {
    id: "print-8",
    category: "Printing",
    question:
      "A shop owner says, 'The print looks a little soft today, but I changed three settings at once so I’m not sure why.' What is the biggest problem with that approach?",
    options: [
      "The powder stage is the only thing that matters",
      "Changing multiple variables at once makes it harder to isolate the cause",
      "The shirt brand will fix it later",
      "Soft prints are always caused by humidity only",
    ],
    correctAnswer: 1,
    explanation:
      "When several changes happen at once, cause-and-effect becomes much harder to identify.",
  },
  {
    id: "print-9",
    category: "Printing",
    question:
      "Why is it risky to assume a printer issue is actually a pressing problem before checking print quality closely?",
    options: [
      "Because cured powder fixes weak output automatically",
      "Because the RIP software controls press pressure",
      "Because flaws created during printing can survive all the way to the finished garment",
      "Because pressing never affects DTF results",
    ],
    correctAnswer: 2,
    explanation:
      "If the transfer starts with poor output, pressing may reveal the issue rather than create it.",
  },
  {
    id: "print-10",
    category: "Printing",
    question:
      "A user sees inconsistent white density from one section of the film to another. Which area deserves immediate attention?",
    options: [
      "Whether the artwork was named correctly",
      "The customer’s shirt size",
      "Final press cover sheet texture only",
      "White channel behavior and print consistency",
    ],
    correctAnswer: 3,
    explanation:
      "Inconsistent white density points back toward white ink behavior, channel performance, or print consistency problems.",
  },
  {
    id: "print-11",
    category: "Printing",
    question:
      "If a known-good file suddenly prints worse than usual, what is the smartest first interpretation?",
    options: [
      "Something in the print workflow likely changed",
      "The press definitely caused it",
      "The customer file is still the most likely cause",
      "The garment blank changed the print on film",
    ],
    correctAnswer: 0,
    explanation:
      "When a known-good test changes, the printer workflow, maintenance state, or current settings deserve attention first.",
  },
  {
    id: "print-12",
    category: "Printing",
    question:
      "Why is a known-good test file valuable during troubleshooting?",
    options: [
      "It removes static from film",
      "It helps separate machine behavior from artwork-specific problems",
      "It replaces nozzle checks",
      "It automatically fixes ink flow",
    ],
    correctAnswer: 1,
    explanation:
      "A known-good file helps isolate whether the issue is tied to the machine workflow or the supplied artwork.",
  },
  {
    id: "print-13",
    category: "Printing",
    question:
      "If white opacity changes during a longer production run, what deserves immediate suspicion?",
    options: [
      "Artwork file naming",
      "White system consistency",
      "Customer garment size",
      "Final press pressure",
    ],
    correctAnswer: 1,
    explanation:
      "When white output changes during a run, the white system and print consistency should be checked first.",
  },
  {
    id: "print-14",
    category: "Printing",
    question:
      "What is one reason to avoid calling a single lucky print 'problem solved'?",
    options: [
      "Because all good prints are misleading",
      "Because powder decides everything instead",
      "Because true process stability must repeat, not just happen once",
      "Because one print always means the RIP is wrong",
    ],
    correctAnswer: 2,
    explanation:
      "A stable workflow must repeat consistently, not just produce one decent result by chance.",
  },
  {
    id: "print-15",
    category: "Printing",
    question:
      "Which answer best explains why film movement matters during printing?",
    options: [
      "Because it controls press heat",
      "Because feed inconsistency can create drift, skew, or print instability",
      "Because it only affects artwork prep",
      "Because it replaces color profiling",
    ],
    correctAnswer: 1,
    explanation:
      "The film path affects print placement and consistency, so unstable feeding can show up in visible output problems.",
  },
  {
    id: "print-16",
    category: "Printing",
    question:
      "Why is it risky to start production without checking recent machine behavior first?",
    options: [
      "Because you may waste material before catching a consistency issue",
      "Because artwork prep becomes irrelevant in production",
      "Because the garment can correct nozzle problems later",
      "Because production runs automatically recalibrate everything",
    ],
    correctAnswer: 0,
    explanation:
      "Catching a print issue before production begins can save film, ink, and wasted time.",
  },
  {
    id: "print-17",
    category: "Printing",
    question:
      "If a print issue gets worse only as a run continues, which mindset is strongest?",
    options: [
      "Ignore the pattern because it is probably random",
      "Blame artwork resolution first",
      "Think about system stability over time, not just one static failure",
      "Assume the final press caused it",
    ],
    correctAnswer: 2,
    explanation:
      "Problems that worsen during longer runs often point toward changing conditions in the print system over time.",
  },
  {
    id: "print-18",
    category: "Printing",
    question:
      "Why is white channel monitoring so important even if the color channels look decent?",
    options: [
      "Because weak white can still ruin opacity and final image quality",
      "Because white never affects the final garment",
      "Because color channels always compensate for white weakness",
      "Because white only matters on white shirts",
    ],
    correctAnswer: 0,
    explanation:
      "The white layer often supports the whole image, so weak white performance can damage the final result even when color seems acceptable.",
  },
  {
    id: "print-19",
    category: "Printing",
    question:
      "What is one of the strongest reasons to document stable working settings?",
    options: [
      "It forces every garment to react the same way",
      "It makes future troubleshooting cleaner when problems appear",
      "It prevents all maintenance forever",
      "It eliminates the need for testing",
    ],
    correctAnswer: 1,
    explanation:
      "Good records help you recognize when a machine or process has drifted away from known-good behavior.",
  },
  {
    id: "print-20",
    category: "Printing",
    question:
      "A print looks slightly off, but you are unsure whether the issue began in artwork or in printing. What is the smartest move?",
    options: [
      "Skip nozzle checks because they slow things down",
      "Change several print settings immediately",
      "Increase powder to see if the print sharpens",
      "Compare a known-good file before making broad assumptions",
    ],
    correctAnswer: 3,
    explanation:
      "A known-good comparison helps isolate whether the issue is in the source file or in the printer workflow.",
  },
];
const powderCuringQuestions: QuizQuestion[] = [
  {
    id: "powder-1",
    category: "Powder & Curing",
    question: "What is the real goal when applying powder to a fresh DTF print?",
    options: [
      "To coat the printed areas evenly without unnecessary excess",
      "To make the design heavier so it feels premium",
      "To cover only the corners of the image",
      "To remove excess white ink",
    ],
    correctAnswer: 0,
    explanation:
      "Powder should bond where it is needed and stay even, without excessive buildup that hurts finish quality.",
  },
  {
    id: "powder-2",
    category: "Powder & Curing",
    question: "What is one likely result of using too much powder?",
    options: [
      "Cleaner fine detail every time",
      "A heavier feel and rougher finish",
      "Automatic durability improvement",
      "Less need for cure control",
    ],
    correctAnswer: 1,
    explanation:
      "Excess powder creates a thicker, heavier transfer and can reduce quality.",
  },
  {
    id: "powder-3",
    category: "Powder & Curing",
    question:
      "If the powder is under-cured before pressing, what outcome becomes more likely?",
    options: [
      "Sharper artwork edges",
      "Improved stretch performance",
      "Weak or inconsistent bonding during transfer",
      "Less need for pressure",
    ],
    correctAnswer: 2,
    explanation:
      "Under-curing prevents proper adhesive activation.",
  },
  {
    id: "powder-4",
    category: "Powder & Curing",
    question: "Which result most strongly suggests uneven powder coverage?",
    options: [
      "A perfect, even finish",
      "Improved opacity everywhere",
      "Stronger film tracking",
      "Patchy hold or inconsistent adhesion",
    ],
    correctAnswer: 3,
    explanation:
      "Uneven powder leads directly to uneven adhesion.",
  },
  {
    id: "powder-5",
    category: "Powder & Curing",
    question: "During curing, what are you trying to avoid?",
    options: [
      "Both under-curing and over-baking",
      "Watching the film closely",
      "Using steady heat",
      "Letting adhesive activate",
    ],
    correctAnswer: 0,
    explanation:
      "Both extremes hurt final performance.",
  },
  {
    id: "powder-6",
    category: "Powder & Curing",
    question:
      "A transfer feels stiff and thick after pressing. What is the likely cause?",
    options: [
      "Only artwork resolution",
      "Powder amount or cure handling",
      "The file name",
      "The monitor brightness",
    ],
    correctAnswer: 1,
    explanation:
      "Too much powder or improper cure leads to heavy feel.",
  },
  {
    id: "powder-7",
    category: "Powder & Curing",
    question:
      "Why is guessing cure quality dangerous?",
    options: [
      "Because cure affects bonding and durability",
      "Because pressing fixes everything",
      "Because powder only matters on cotton",
      "Because cure never affects results",
    ],
    correctAnswer: 0,
    explanation:
      "Cure is one of the most critical stages in DTF.",
  },
  {
    id: "powder-8",
    category: "Powder & Curing",
    question:
      "A design sticks in some areas but lifts in others. What is most likely?",
    options: [
      "Uneven powder or cure",
      "The file name",
      "The shirt color",
      "Monitor brightness",
    ],
    correctAnswer: 0,
    explanation:
      "Localized adhesion issues usually point to powder/cure inconsistency.",
  },
  {
    id: "powder-9",
    category: "Powder & Curing",
    question:
      "Why does powder control matter for fine detail?",
    options: [
      "It affects finish and edge clarity",
      "It only affects large shapes",
      "It has no effect",
      "It only affects cotton",
    ],
    correctAnswer: 0,
    explanation:
      "Poor powder handling can muddy detail.",
  },
  {
    id: "powder-10",
    category: "Powder & Curing",
    question:
      "What is the best mindset for powder/cure troubleshooting?",
    options: [
      "Treat them as critical stages",
      "Ignore unless failing",
      "Assume printing is the issue",
      "Only adjust after complaints",
    ],
    correctAnswer: 0,
    explanation:
      "These are core process stages.",
  },

  // NEW 10 (balanced answers)

  {
    id: "powder-11",
    category: "Powder & Curing",
    question:
      "If powder sticks outside the design area, what does that indicate?",
    options: [
      "Too much static or loose ink residue",
      "Perfect powder application",
      "Low press pressure",
      "Proper curing",
    ],
    correctAnswer: 0,
    explanation:
      "Powder sticking outside print usually means contamination or static.",
  },
  {
    id: "powder-12",
    category: "Powder & Curing",
    question:
      "What happens if powder is not fully melted during curing?",
    options: [
      "Better stretch",
      "Weak bonding during pressing",
      "Sharper colors",
      "Improved peel",
    ],
    correctAnswer: 1,
    explanation:
      "Incomplete melt = poor adhesive activation.",
  },
  {
    id: "powder-13",
    category: "Powder & Curing",
    question:
      "Why is visual shine sometimes misleading during curing?",
    options: [
      "It always means over-cure",
      "It guarantees proper cure",
      "It can look cured but still be under-cured",
      "It shows perfect adhesion",
    ],
    correctAnswer: 2,
    explanation:
      "Visual cues alone can be misleading.",
  },
  {
    id: "powder-14",
    category: "Powder & Curing",
    question:
      "If powder appears clumpy, what is likely happening?",
    options: [
      "Low humidity",
      "Perfect distribution",
      "Uneven application or moisture issues",
      "Strong adhesion",
    ],
    correctAnswer: 2,
    explanation:
      "Clumping often indicates poor environment or application.",
  },
  {
    id: "powder-15",
    category: "Powder & Curing",
    question:
      "What is a key indicator of over-curing?",
    options: [
      "Transfer becomes brittle or dull",
      "Stronger adhesion always",
      "Sharper detail",
      "Lower press temp needed",
    ],
    correctAnswer: 0,
    explanation:
      "Over-curing can damage adhesive performance.",
  },
  {
    id: "powder-16",
    category: "Powder & Curing",
    question:
      "Why is consistent powder thickness important?",
    options: [
      "It ensures even bonding",
      "It affects file resolution",
      "It changes print DPI",
      "It fixes nozzle issues",
    ],
    correctAnswer: 0,
    explanation:
      "Even thickness = even adhesion.",
  },
  {
    id: "powder-17",
    category: "Powder & Curing",
    question:
      "If adhesion fails only on thin areas, what should you check?",
    options: [
      "Powder coverage on fine detail",
      "Garment size",
      "File name",
      "Screen brightness",
    ],
    correctAnswer: 0,
    explanation:
      "Thin areas often receive less powder.",
  },
  {
    id: "powder-18",
    category: "Powder & Curing",
    question:
      "What role does curing temperature play?",
    options: [
      "Activates adhesive properly",
      "Controls file resolution",
      "Fixes artwork edges",
      "Changes garment color",
    ],
    correctAnswer: 0,
    explanation:
      "Heat activates adhesive bonding properties.",
  },
  {
    id: "powder-19",
    category: "Powder & Curing",
    question:
      "Why should powder be shaken off evenly?",
    options: [
      "To avoid buildup and uneven bonding",
      "To improve nozzle flow",
      "To increase DPI",
      "To reduce press pressure",
    ],
    correctAnswer: 0,
    explanation:
      "Even removal prevents thick spots.",
  },
  {
    id: "powder-20",
    category: "Powder & Curing",
    question:
      "What is the biggest mistake beginners make in this stage?",
    options: [
      "Treating powder/cure as minor steps",
      "Over-checking results",
      "Using too little heat always",
      "Testing too often",
    ],
    correctAnswer: 0,
    explanation:
      "Beginners underestimate how critical this stage is.",
  },
];

const pressingQuestions: QuizQuestion[] = [
  {
    id: "press-1",
    category: "Pressing",
    question:
      "If a transfer does not stick properly, what is one of the first areas to review?",
    options: [
      "Press settings like time, temperature, and pressure",
      "File extension",
      "Product page",
      "Photoshop settings",
    ],
    correctAnswer: 0,
    explanation:
      "Press settings directly affect adhesion.",
  },
  {
    id: "press-2",
    category: "Pressing",
    question: "Why is pressure important?",
    options: [
      "It bonds adhesive into fabric",
      "It changes nozzle output",
      "It replaces curing",
      "It creates white ink",
    ],
    correctAnswer: 0,
    explanation:
      "Pressure ensures proper bonding contact.",
  },
  {
    id: "press-3",
    category: "Pressing",
    question:
      "What happens if temperature is too low?",
    options: [
      "Weak bonding",
      "Sharper detail",
      "Better stretch",
      "Faster peel",
    ],
    correctAnswer: 0,
    explanation:
      "Low heat = weak adhesion.",
  },
  {
    id: "press-4",
    category: "Pressing",
    question:
      "What happens if temperature is too high?",
    options: [
      "Scorching or damage",
      "Perfect bonding always",
      "Better durability",
      "Stronger white",
    ],
    correctAnswer: 0,
    explanation:
      "Too much heat damages material.",
  },
  {
    id: "press-5",
    category: "Pressing",
    question:
      "Why do a final press?",
    options: [
      "Smooth and finish transfer",
      "Add powder",
      "Fix nozzles",
      "Change DPI",
    ],
    correctAnswer: 0,
    explanation:
      "Final press improves finish and seal.",
  },

  // NEW 15 balanced

  {
    id: "press-6",
    correctAnswer: 1,
    category: "Pressing",
    question: "What causes weak edges after pressing?",
    options: [
      "Artwork only",
      "Uneven pressure/contact",
      "File naming",
      "Monitor brightness",
    ],
    explanation: "Edges often fail due to uneven contact.",
  },
  {
    id: "press-7",
    correctAnswer: 2,
    category: "Pressing",
    question: "Why is a good press not always proof of a good process?",
    options: [
      "Because pressing never matters",
      "Because powder fixes everything",
      "Some issues appear later after wear",
      "Because peel type decides everything",
    ],
    explanation: "Durability issues can show later.",
  },
  {
    id: "press-8",
    correctAnswer: 0,
    category: "Pressing",
    question: "Best way to adjust press issues?",
    options: [
      "Change one variable at a time",
      "Change everything",
      "Ignore it",
      "Add powder",
    ],
    explanation: "Single-variable testing is key.",
  },
  {
    id: "press-9",
    correctAnswer: 3,
    category: "Pressing",
    question: "Why does garment type matter?",
    options: [
      "It doesn’t",
      "Only color matters",
      "Only cotton matters",
      "Materials respond differently to heat and pressure",
    ],
    explanation: "Fabric affects adhesion and feel.",
  },
  {
    id: "press-10",
    correctAnswer: 2,
    category: "Pressing",
    question: "Same transfer, different result on two shirts—why?",
    options: [
      "File corrupted",
      "White ink changed",
      "Garment differences",
      "Powder irrelevant",
    ],
    explanation: "Different fabrics react differently.",
  },

  {
    id: "press-11",
    correctAnswer: 1,
    category: "Pressing",
    question: "What helps isolate garment issues?",
    options: [
      "Changing powder",
      "Using a known-good blank",
      "Changing artwork",
      "Increasing DPI",
    ],
    explanation: "Known blanks isolate variables.",
  },
  {
    id: "press-12",
    correctAnswer: 0,
    category: "Pressing",
    question: "Weak adhesion near seams means?",
    options: [
      "Uneven pressure/contact",
      "Bad artwork",
      "Wrong file type",
      "Printer issue",
    ],
    explanation: "Seams affect pressure.",
  },
  {
    id: "press-13",
    correctAnswer: 3,
    category: "Pressing",
    question: "What improves finish quality?",
    options: [
      "More powder",
      "Higher DPI",
      "Lower resolution",
      "Proper final press technique",
    ],
    explanation: "Final press smooths result.",
  },
  {
    id: "press-14",
    correctAnswer: 2,
    category: "Pressing",
    question: "What causes inconsistent adhesion across a design?",
    options: [
      "File naming",
      "Monitor brightness",
      "Uneven pressure distribution",
      "Color profile",
    ],
    explanation: "Uneven pressure = uneven bonding.",
  },
  {
    id: "press-15",
    correctAnswer: 1,
    category: "Pressing",
    question: "Why test press settings?",
    options: [
      "To increase speed",
      "To dial in consistent results",
      "To change artwork",
      "To reduce powder",
    ],
    explanation: "Testing ensures repeatability.",
  },
  {
    id: "press-16",
    correctAnswer: 0,
    category: "Pressing",
    question: "What happens with too much pressure?",
    options: [
      "Can damage or distort transfer",
      "Always improves adhesion",
      "Fixes all issues",
      "Improves DPI",
    ],
    explanation: "Too much pressure can hurt results.",
  },
  {
    id: "press-17",
    correctAnswer: 2,
    category: "Pressing",
    question: "What affects peel behavior?",
    options: [
      "File name",
      "Monitor brightness",
      "Temperature and timing",
      "Garment tag",
    ],
    explanation: "Peel depends on heat/time.",
  },
  {
    id: "press-18",
    correctAnswer: 3,
    category: "Pressing",
    question: "Why is consistency important?",
    options: [
      "For aesthetics only",
      "For speed only",
      "For cost only",
      "For repeatable results",
    ],
    explanation: "Consistency = reliability.",
  },
  {
    id: "press-19",
    correctAnswer: 1,
    category: "Pressing",
    question: "What should you do if results vary?",
    options: [
      "Ignore it",
      "Standardize process",
      "Change everything",
      "Blame printer",
    ],
    explanation: "Consistency fixes variation.",
  },
  {
    id: "press-20",
    correctAnswer: 0,
    category: "Pressing",
    question: "Best mindset for pressing?",
    options: [
      "Treat as critical step",
      "Ignore it",
      "Rush it",
      "Guess settings",
    ],
    explanation: "Pressing is a core process stage.",
  },
];
const troubleshootingQuestions: QuizQuestion[] = [
  {
    id: "trouble-1",
    category: "Troubleshooting",
    question: "What is the smartest general troubleshooting habit when something goes wrong?",
    options: [
      "Change one variable at a time",
      "Change everything quickly and compare mentally",
      "Blame the film first",
      "Skip documentation and rely on memory",
    ],
    correctAnswer: 0,
    explanation:
      "Single-variable troubleshooting makes the real cause much easier to identify.",
  },
  {
    id: "trouble-2",
    category: "Troubleshooting",
    question:
      "Colors look flatter than expected on a dark garment. Which answer is one of the strongest places to investigate?",
    options: [
      "The shipping label",
      "White underbase support and related print quality",
      "The product page font",
      "The browser used to upload the artwork",
    ],
    correctAnswer: 1,
    explanation:
      "Weak white support often affects color pop, especially on dark garments.",
  },
  {
    id: "trouble-3",
    category: "Troubleshooting",
    question:
      "A print cracks or performs poorly after application. Which answer is the most complete direction to review?",
    options: [
      "Only the file name",
      "Only the artwork size in inches",
      "Cure quality, press settings, and material compatibility",
      "Only whether the printer was turned on first",
    ],
    correctAnswer: 2,
    explanation:
      "Durability issues can come from several stages, especially cure, pressing, and compatibility with the material.",
  },
  {
    id: "trouble-4",
    category: "Troubleshooting",
    question: "Why is keeping notes on repeat issues valuable in a DTF shop?",
    options: [
      "It makes maintenance unnecessary",
      "It prevents all clogged nozzles",
      "It replaces test prints",
      "It helps identify patterns and speeds up future diagnosis",
    ],
    correctAnswer: 3,
    explanation:
      "Good notes help reveal repeat patterns in settings, materials, and problem conditions.",
  },
  {
    id: "trouble-5",
    category: "Troubleshooting",
    question:
      "Why should you look at the full workflow instead of only the stage where the failure became visible?",
    options: [
      "Because problems often start earlier than where they finally show up",
      "Because every DTF issue starts at the press",
      "Because every DTF issue starts in Photoshop",
      "Because visual symptoms always reveal the exact cause instantly",
    ],
    correctAnswer: 0,
    explanation:
      "A symptom may appear at one stage even though the true cause started earlier in prep, printing, curing, or application.",
  },
  {
    id: "trouble-6",
    category: "Troubleshooting",
    question:
      "A transfer fails on only one part of the design repeatedly, not the whole image. What does that suggest?",
    options: [
      "The printer is fully dead",
      "A localized process issue may be involved rather than a total workflow collapse",
      "The shirt size caused a global machine error",
      "The design must be rebranded",
    ],
    correctAnswer: 1,
    explanation:
      "Localized failure can suggest a more targeted problem such as uneven powder, pressure, or detail-specific prep behavior.",
  },
  {
    id: "trouble-7",
    category: "Troubleshooting",
    question:
      "What is the danger of blaming the film every time something goes wrong?",
    options: [
      "It improves troubleshooting speed",
      "Film is always the root cause",
      "It can hide the real issue and delay solving the actual process problem",
      "It helps stabilize white ink",
    ],
    correctAnswer: 2,
    explanation:
      "Over-blaming one variable can keep you from checking the true root cause in the workflow.",
  },
  {
    id: "trouble-8",
    category: "Troubleshooting",
    question:
      "A print issue seems random, but after reviewing notes you discover it happens more often on certain jobs and materials. What is that called in practical troubleshooting?",
    options: [
      "Proof that the issue is impossible to solve",
      "Evidence that the design software is irrelevant",
      "A sign that no testing is needed",
      "A useful pattern that can help narrow the cause",
    ],
    correctAnswer: 3,
    explanation:
      "Pattern recognition is one of the most valuable parts of troubleshooting because it helps reduce guesswork.",
  },
  {
    id: "trouble-9",
    category: "Troubleshooting",
    question:
      "Which answer best reflects expert-level troubleshooting thinking?",
    options: [
      "Verify the symptom, isolate variables, and review the entire process path",
      "Change everything and trust instinct",
      "Retest only when a customer complains",
      "Assume the newest product is automatically the problem",
    ],
    correctAnswer: 0,
    explanation:
      "Strong troubleshooting means verifying what happened, isolating variables, and checking the process from start to finish.",
  },
  {
    id: "trouble-10",
    category: "Troubleshooting",
    question:
      "What is one reason two different operators can get different results from the same setup?",
    options: [
      "DTF machines only work for one person",
      "Small workflow differences in handling, judgment, and consistency can matter",
      "Artwork resolution changes based on who opens the file",
      "Transfers ignore process control when more than one person is involved",
    ],
    correctAnswer: 1,
    explanation:
      "Operator consistency matters. Small differences in prep, powdering, curing, and pressing can create different outcomes.",
  },
  {
    id: "trouble-11",
    category: "Troubleshooting",
    question:
      "Why is a controlled retest more valuable than guessing from one bad sample?",
    options: [
      "Because it helps confirm whether the symptom is repeatable and tied to a real variable",
      "Because it makes all settings permanent",
      "Because it replaces maintenance forever",
      "Because it proves the film is always bad",
    ],
    correctAnswer: 0,
    explanation:
      "A controlled retest helps separate a real repeat issue from a one-off event or bad assumption.",
  },
  {
    id: "trouble-12",
    category: "Troubleshooting",
    question:
      "What is one sign that a shop is improving diagnostically, not just reacting emotionally?",
    options: [
      "They change everything faster",
      "They document symptoms, compare tests, and reduce guesswork",
      "They skip repeat testing",
      "They assume every issue is the printer",
    ],
    correctAnswer: 1,
    explanation:
      "Better diagnosis comes from structured observation, cleaner testing, and fewer emotional guesses.",
  },
  {
    id: "trouble-13",
    category: "Troubleshooting",
    question:
      "If a problem appears random, what is one of the smartest first moves?",
    options: [
      "Look for patterns in material, timing, and workflow",
      "Assume it cannot be solved",
      "Change every setting immediately",
      "Skip documentation",
    ],
    correctAnswer: 0,
    explanation:
      "Many 'random' problems become easier to diagnose once patterns are tracked.",
  },
  {
    id: "trouble-14",
    category: "Troubleshooting",
    question:
      "Why is isolating the stage of failure so important?",
    options: [
      "Because it prevents all future problems",
      "Because it helps narrow where the real cause likely began",
      "Because it makes artwork prep irrelevant",
      "Because it guarantees the fix is obvious",
    ],
    correctAnswer: 1,
    explanation:
      "The visible failure may not be where the problem started, so stage isolation matters.",
  },
  {
    id: "trouble-15",
    category: "Troubleshooting",
    question:
      "A transfer fails only on one garment brand but works on another. What does that strongly suggest?",
    options: [
      "The design file is corrupted",
      "The printer is randomly broken",
      "Material compatibility or garment-specific process behavior",
      "The nozzle check no longer matters",
    ],
    correctAnswer: 2,
    explanation:
      "When one garment works and another fails, compatibility becomes a major suspect.",
  },
  {
    id: "trouble-16",
    category: "Troubleshooting",
    question:
      "What is the danger of changing settings before verifying the actual symptom clearly?",
    options: [
      "It can create confusion and bury the true cause",
      "It always improves speed",
      "It makes note-taking easier",
      "It guarantees a faster diagnosis",
    ],
    correctAnswer: 0,
    explanation:
      "If you do not verify the symptom first, your changes may add noise instead of helping.",
  },
  {
    id: "trouble-17",
    category: "Troubleshooting",
    question:
      "Why should a known-good sample matter during troubleshooting?",
    options: [
      "It gives you a stable comparison point",
      "It replaces test prints",
      "It makes pressure irrelevant",
      "It forces every garment to behave the same",
    ],
    correctAnswer: 0,
    explanation:
      "Known-good samples help separate current problems from assumptions.",
  },
  {
    id: "trouble-18",
    category: "Troubleshooting",
    question:
      "What is one reason expert troubleshooters document what did NOT work?",
    options: [
      "Because failed tests still narrow the search and prevent repeating bad guesses",
      "Because bad notes are more useful than good ones",
      "Because wrong answers fix the printer later",
      "Because documentation only matters for managers",
    ],
    correctAnswer: 0,
    explanation:
      "Failed tests are still valuable because they eliminate possibilities and reduce repeated guessing.",
  },
  {
    id: "trouble-19",
    category: "Troubleshooting",
    question:
      "If two symptoms appear together, what is the smartest habit?",
    options: [
      "Assume they are unrelated",
      "Check whether they share a common root in the workflow",
      "Ignore one and fix the other",
      "Blame the newest material automatically",
    ],
    correctAnswer: 1,
    explanation:
      "Multiple symptoms can sometimes point back to the same upstream cause.",
  },
  {
    id: "trouble-20",
    category: "Troubleshooting",
    question:
      "What best describes the mindset of strong DTF troubleshooting?",
    options: [
      "Structured, repeatable, and evidence-based",
      "Fast, emotional, and instinct-only",
      "Random but confident",
      "Focused only on the most visible symptom",
    ],
    correctAnswer: 0,
    explanation:
      "Strong troubleshooting is built on observation, repeatability, and clean testing instead of panic and guesswork.",
  },
];

export const quizSections: QuizSection[] = [
  {
    id: "artwork-prep",
    title: "Artwork Prep",
    description:
      "Learn how file quality, edge cleanup, transparency, gradients, and prep habits affect the final transfer.",
    questions: artworkPrepQuestions,
  },
  {
    id: "printing",
    title: "Printing",
    description:
      "Train on nozzle health, white behavior, film tracking, long-run stability, and print-stage consistency.",
    questions: printingQuestions,
  },
  {
    id: "powder-curing",
    title: "Powder & Curing",
    description:
      "Review powder coverage, clumping, cure judgment, transfer feel, and adhesive behavior.",
    questions: powderCuringQuestions,
  },
  {
    id: "pressing",
    title: "Pressing",
    description:
      "Focus on time, temperature, pressure, contact, final press technique, and garment response.",
    questions: pressingQuestions,
  },
  {
    id: "troubleshooting",
    title: "Troubleshooting",
    description:
      "Build better diagnostic thinking with variable isolation, pattern recognition, and workflow review.",
    questions: troubleshootingQuestions,
  },
];

export const quizQuestions: QuizQuestion[] = quizSections.flatMap(
  (section) => section.questions
);

export const getQuizSectionById = (
  sectionId: string
): QuizSection | undefined =>
  quizSections.find((section) => section.id === sectionId);

export const getQuestionsForSection = (sectionId: string): QuizQuestion[] =>
  getQuizSectionById(sectionId)?.questions ?? [];
